import * as React from 'react';

import { cn } from '@/lib/utils';

/* Segmentado — escolha de UMA opção entre poucas, tudo visível ao mesmo tempo.
 *
 * Por que não `Tabs` do shadcn: aba é NAVEGAÇÃO (troca de painel), este é um
 * CONTROLE DE FORMULÁRIO (define um dado que será salvo, ex.: "comissão incide
 * sobre TOTAL ou sobre SERVIÇO"). Usar aba aqui mentiria para o leitor de tela.
 *
 * Por que <input type="radio"> escondido em vez de botões com role="radio":
 * navegação por setas, seleção por espaço, agrupamento por `name` e submissão
 * em formulário vêm de graça do navegador. Menos código nosso, mais correto.
 *
 * ── DAR NOME AO GRUPO SEM MOSTRAR RÓTULO ─────────────────────────────────
 * Um grupo de rádios sem nome é anunciado como "grupo" e o leitor de tela não
 * diz de que escolha se trata. Acontece sempre que o rótulo já está dito pelo
 * contexto ao redor ("A comissão incide sobre" numa frase acima) e repeti-lo
 * seria ruído visual. Duas saídas, as duas opcionais:
 *
 *   rotulo="…" rotuloOculto   a MELHOR: o texto existe de verdade, só não é
 *                             pintado. Nome e rótulo nunca divergem, porque
 *                             são a mesma string.
 *   aria-label="…"            quando não há rótulo nenhum a esconder.
 *
 * Sem nenhuma das duas o componente se comporta exatamente como antes. */

export type OpcaoSegmentada<T extends string> = {
  valor: T;
  rotulo: string;
  /** Uma linha no title/tooltip nativo, para o caso não óbvio. */
  descricao?: string | undefined;
  desabilitada?: boolean | undefined;
};

export type SegmentadoProps<T extends string> = {
  opcoes: ReadonlyArray<OpcaoSegmentada<T>>;
  /** `null` = nada marcado, para a escolha que o usuário tem de fazer. */
  valor: T | null;
  aoMudar: (valor: T) => void;
  /** Rótulo do grupo. Vira o <legend>, lido antes das opções. */
  rotulo?: string | undefined;
  /** Mantém o <legend> para o leitor de tela e o tira da tela. */
  rotuloOculto?: boolean | undefined;
  /** Nome do grupo quando não há `rotulo` a esconder. */
  'aria-label'?: string | undefined;
  /** Nome do grupo de rádios. Gerado quando omitido. */
  nome?: string | undefined;
  tamanho?: 'pequeno' | 'medio' | undefined;
  className?: string | undefined;
};

export function Segmentado<T extends string>({
  opcoes,
  valor,
  aoMudar,
  rotulo,
  rotuloOculto = false,
  nome,
  tamanho = 'medio',
  className,
  'aria-label': rotuloAcessivel,
}: SegmentadoProps<T>) {
  const nomeAuto = React.useId();
  const nomeGrupo = nome ?? nomeAuto;

  return (
    <fieldset
      className={cn('grid gap-2', className)}
      {...(rotuloAcessivel === undefined ? {} : { 'aria-label': rotuloAcessivel })}
    >
      {/* O <legend> visível é o mesmo degrau do rótulo de campo: nomear o
          grupo e nomear um campo são a mesma coisa para quem lê. */}
      {rotulo ? (
        <legend className={cn(rotuloOculto ? 'sr-only' : 'rotulo mb-1.5')}>{rotulo}</legend>
      ) : null}

      {/* Trilho de areia (o campo inerte) com a opção escolhida em nata: o
          contraste de CAMPO é o que marca a escolha, não uma borda. */}
      <div className="bg-areia inline-flex w-fit gap-1 rounded-pilula p-1">
        {opcoes.map((opcao) => {
          const marcada = opcao.valor === valor;
          return (
            <label
              key={opcao.valor}
              title={opcao.descricao}
              className={cn(
                'relative inline-flex cursor-pointer items-center justify-center rounded-pilula font-medium transition-colors select-none',
                tamanho === 'pequeno' ? 'h-7 px-3 text-nota' : 'h-8 px-4 text-apoio',
                // A escolha é marcada por TRÊS coisas ao mesmo tempo, e não é
                // exagero: campo mais claro que o trilho, contorno de ação
                // (--acao-fio separa 3,5:1 da nata, o que a WCAG 1.4.11 exige
                // de um controle) e tinta de ação no texto. Antes era só o
                // campo de nata sobre o trilho de areia: 1,16:1, que num
                // monitor de escritório com brilho baixo simplesmente
                // desaparecia, e a pessoa não sabia o que tinha marcado.
                marcada
                  ? 'bg-nata text-acao-forte ring-acao-fio ring-1'
                  : 'text-tinta-2 hover:text-tinta',
                opcao.desabilitada ? 'pointer-events-none opacity-50' : undefined,
                // O anel de foco vive no rótulo porque o rádio está escondido.
                'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
              )}
            >
              <input
                type="radio"
                name={nomeGrupo}
                value={opcao.valor}
                checked={marcada}
                disabled={opcao.desabilitada}
                onChange={() => aoMudar(opcao.valor)}
                className="sr-only"
              />
              {opcao.rotulo}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
